/**
 * What the location's PMS can do, as far as the agent UI is concerned.
 *
 * The only capability the UI cares about today is write-back: the call-summary popup must
 * not open for a PMS that cannot store what the agent types. Submitting into such a popup
 * flips it to "submitted" and closes it, so the agent sees a successful save while the note
 * is dropped server-side — the failure is invisible from the agent's seat.
 *
 * This fact belongs to the BACKEND (PMS_CAPABILITIES in helpers/helper.js), which is why
 * `write_back_supported` now rides along with `pms_provider` on both channels the UI reads:
 * the voice agent-notification payload and the /monumentUnitGroups response the video modal
 * fetches. Gate on that boolean, not on a provider name — otherwise the frontend keeps a
 * second copy of the capability table that has to be found and edited in lock-step, which is
 * exactly the trap this replaces.
 */

/**
 * Providers with no PMS write-back, used ONLY when the backend did not send the flag.
 *
 * The two repos deploy independently, so a frontend build can reach an older backend that
 * predates `write_back_supported`. Defaulting to "supported" in that window would reopen the
 * Hummingbird popup and silently drop summaries, so fall back to the provider name instead.
 * DELETE this list once every environment runs a backend that sends the flag — at that point
 * the capability lives in exactly one place.
 *
 * 'other' (custom PMS) has no integration at all. It is also needed for outbound calls: their
 * location comes from /agent/my-locations, which carries pms_provider but not the flag.
 */
const PROVIDERS_WITHOUT_WRITE_BACK = ["hummingbird", "other"];

/**
 * Can this location's PMS accept a call-summary write-back?
 *
 * @param source Any object carrying the backend's PMS fields — the voice callData or the
 *   /monumentUnitGroups response body. Both expose `write_back_supported` and `pms_provider`.
 */
export const supportsPmsWriteBack = (source: any): boolean => {
    if (typeof source?.write_back_supported === "boolean") return source.write_back_supported;
    // Backend predates the flag (or sent null): fall back to the provider name.
    return !PROVIDERS_WITHOUT_WRITE_BACK.includes(source?.pms_provider);
};
